import { Fragment, useEffect, useState } from "react";
import { Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { Certificate, CertificateStatus, CertificationGeneration } from "../../../Physician/Models/Certificate";
import AbpnSpinner from "../../../Shared/Components/AbpnSpinner";
import Physician from "../../../Physician/Models/Physician";
import { getDateWithOffset } from "../../../Shared/Utility/GetDateWithOffset";

interface IProps {
    physician: Physician,
    certificateGroup: Certificate[];
    onSave: (updatedCertificate: Certificate) => void;
}

export function CertificateRow(props: IProps) {
    const [displayCertificate, setDisplayCertificate] = useState<Certificate>(null);
    const [expiredCertificatesByDate, setExpiredCertificatesByDate] = useState<Certificate[]>([]);

    useEffect(() => {
        let activeCertificate = props.certificateGroup.find(_ => _.certificateStatus === CertificateStatus.Active);
        let inactiveCertificatesByDate = props.certificateGroup.filter(_ => _.certificateStatus === CertificateStatus.Inactive).sort(
            (a, b) => getDateWithOffset(b.awardDate).getTime() - getDateWithOffset(a.awardDate).getTime()
        );
        let displayCert = activeCertificate ?? inactiveCertificatesByDate[0];
        setDisplayCertificate(displayCert);
        setExpiredCertificatesByDate(inactiveCertificatesByDate)
    }, []);

    var lifetimeInfoIconContent = (<div>
        <span>Non-Time Limited (Lifetime) certificates are not required to participate in the Continuing Certification (CC) program.</span>
        <br /><br />
        <span>
            Diplomates who wish to participate in CC for a non-time limited (lifetime) certification will need to enroll in the traditional
            recertification/CC Exam OR the Article Based Continuing Certification (ABCC) pathway. Diplomates will pay the first annual fee for that
            certification at the time of enrollment, and will then be listed as "Meeting" CC/MOC requirements. Diplomates will have the next three years
            to pass the recertification/CC exam OR successfully complete 30 article exams, and complete the CC requirements.
        </span>
        <br /><br />
        <span>
            Diplomates who choose the article pathway will need to continue to complete article exams each three-year block, in addition to
            satisfying the CC requirements. For more information about the ABCC pathway, please visit
            <a target="_blank" rel='noreferrer' href={"https://www.abpn.org/maintain-certification/article-based-continuing-certification-abcc-pathway/"}> abpn.org</a>.
        </span>
    </div>);

    var ccLabel = "Continuing Certification Status";
    var mocLabel = "MOC Status";

    let isDisplayCertificateActive = displayCertificate?.certificateStatus == CertificateStatus.Active;

    let showInfoIcon = true;
    var infoIconContent: any;

    let showStatus = isDisplayCertificateActive;
    let ccMocStatusLabel: string;

    var displayEndDateLabel = false;
    var endDateLabel: string;
    var certificateEndDateDisplay: string; 


    switch (displayCertificate?.certificationGeneration) {
        case CertificationGeneration.TenYear:
            showInfoIcon = false;
            ccMocStatusLabel = mocLabel;
            displayEndDateLabel = true;

            break;
        case CertificationGeneration.Lifetime:
            showStatus = true;
            infoIconContent = lifetimeInfoIconContent;
            ccMocStatusLabel = mocLabel;

            if (props.physician.currentCCEnrollment != null) {
                ccMocStatusLabel = ccLabel;
            }
            break;
        case CertificationGeneration.Cc:
            ccMocStatusLabel = ccLabel;
            break;
        default:
            break;
    }
    var isActiveCC = displayCertificate?.certificationGeneration === CertificationGeneration.Cc 
                            && displayCertificate.certificateStatus === CertificateStatus.Active;

    if (!isActiveCC && displayCertificate?.expirationDate && displayCertificate?.certificationGeneration != CertificationGeneration.Lifetime) {
        showInfoIcon = false;
        endDateLabel = "Expiration";
        displayEndDateLabel = true;
        certificateEndDateDisplay = getDateWithOffset(displayCertificate.expirationDate).toLocaleDateString();
    }

    let displayIsCertified =
        (isDisplayCertificateActive ?
            <div className="pp-font-size-smaller">Certified</div>
            :
            <div className="pp-font-size-smaller text-danger">Not Certified</div>
        );
        
    var infoIcon = (<OverlayTrigger
        trigger="click"
        placement={"top"}
        rootClose={true}
        overlay={
            <Tooltip id="contactAddress" bsPrefix="pp-tooltip">
                {infoIconContent}
            </Tooltip>
        }>
        <i className="las la-info-circle ml-1 la-lg"></i>
    </OverlayTrigger>);

    if (props.physician.isRetired) {
        if (displayCertificate?.expirationDate) {
            displayEndDateLabel = true;
            endDateLabel = "Retired Notification"
            certificateEndDateDisplay =  getDateWithOffset(displayCertificate.expirationDate).toLocaleDateString();
        }

        showInfoIcon = false;
        displayIsCertified = <div className="pp-font-size-smaller">Certified - Retired</div>
    }

    if (displayCertificate?.isHPM) displayIsCertified = (<div className="pp-font-size-smaller">ABPN issued certificates in Hospice and Palliative Medicine (HPM) from 2008-2021.  In 2022, HPM certificates issued by ABPN were transitioned to the American Board of Internal Medicine (ABIM) for initial certification and continuing certification.  
                        For the current status of this certificate, please visit<a href="https://www.abim.org/" target="_blank"> ABIM.org</a>.</div>)

    if (displayCertificate == null) return <AbpnSpinner />
    return (
        <Fragment>
            <Row className="mb-3">
                <Col className="mb-1" xs={12} md={12} lg={4} xl={4}>
                    <div className="font-weight-bolder mb-1 pp-font-size-smaller">
                        {displayCertificate.certificationName.toUpperCase()}
                        {showInfoIcon && displayCertificate.certificationGeneration == CertificationGeneration.Lifetime &&
                            infoIcon
                        }
                    </div>
                    {displayIsCertified}
                </Col>
                <Col xs={12} lg={8}>
                    <Row>
                        <Col xs={12} lg={displayEndDateLabel ? 3 : 6}>
                            <div className="font-weight-bolder mb-1 pp-font-size-smaller">Awarded</div>
                            <div className="pp-font-size-smaller">
                                {getDateWithOffset(displayCertificate.awardDate).toLocaleDateString()}
                            </div>
                        </Col>
                        {displayEndDateLabel &&
                            <Fragment>
                                <Col xs={12} lg={3}>
                                    <div className="font-weight-bolder mb-1 pp-font-size-smaller">{endDateLabel}
                                        {showInfoIcon && displayCertificate.certificationGeneration == CertificationGeneration.Cc &&
                                            infoIcon
                                        }
                                    </div>
                                    <div className="pp-font-size-smaller">{certificateEndDateDisplay}</div>
                                </Col>
                            </Fragment>
                        }
                        <Col xs={12} lg={6}>
                            <div className="font-weight-bolder mb-1 pp-font-size-smaller">Certificate Number</div>
                            <div className="pp-font-size-smaller">{displayCertificate.certificateNumber}</div>
                        </Col>
                    </Row>
                    {showStatus &&
                        <Fragment>
                            <hr />
                            <Row>
                                <Col>
                                    <div className="font-weight-bolder mb-1 pp-font-size-smaller">{ccMocStatusLabel}</div>
                                    <div className="pp-font-size-smaller">{displayCertificate.ccStatusDisplay}</div>
                                </Col>
                            </Row>
                        </Fragment>
                    }
                    {expiredCertificatesByDate.length > 0 &&
                        <Fragment>
                            <hr />
                            <Row>
                                <Col>
                                    <div className="font-weight-bolder mb-1 pp-font-size-smaller">Certification History</div>
                                    {displayCertificate.certificateStatus == CertificateStatus.Active &&
                                        <div className="pp-font-size-smaller">Certified on {getDateWithOffset(displayCertificate.awardDate).toLocaleDateString()} | Certificate Number {displayCertificate.certificateNumber}</div>}
                                    {expiredCertificatesByDate.map(_ =>
                                        <div className="pp-font-size-smaller">Certified from {getDateWithOffset(_.awardDate).toLocaleDateString()} to {getDateWithOffset(_.expirationDate).toLocaleDateString()} | Certificate Number {_.certificateNumber}</div>
                                    )}
                                </Col>
                            </Row>
                        </Fragment>
                    }
                </Col>
            </Row>
            <hr />
        </Fragment>
    );
}